import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  NoPreloading,
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  Routes,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { environment } from '../environments/environment';
import { LayoutMainComponent } from './components/masterpage';
import { MobileLayoutComponent } from './components/masterpage/mobile-layout/mobile-layout.component';
import { ProgramBadgeWrapperComponent } from './routes/my-profile/routes/program-badge-wrapper/program-badge-wrapper.component';
import { NoContentComponent } from './routes/no-content/no-content.component';
import { AuthGuard } from './shared/guards/auth.guard';

class AppRouteReuseStrategy extends BaseRouteReuseStrategy {
  public override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig || (future.data['reuseComponent'] && curr.component === future.component);
  }
}

const APP_ROUTES: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./routes/login/auth.routes').then(r => r.LOGIN_ROUTES),
    data: {
      title: 'Log in to RealtyBid for Real Estate Auctions & Foreclosures',
      description:
        'Log in to RealtyBid, home of online real estate auctions and foreclosure listings across the United States. Browse online house auctions and place your bid.',
    },
  },
  {
    path: 'sign-up',
    loadChildren: () => import('./routes/login/auth.routes').then(r => r.SIGNUP_ROUTES),
    data: {
      title: 'Sign Up for RealtyBid & Find Real Estate Auctions & Foreclosures',
      description: 'Sign up for RealtyBid, home of online real estate auctions & foreclosure listings',
    },
  },
  {
    path: 'forgot-password',
    pathMatch: 'full',
    loadChildren: () => import('./routes/forgot-password/forgot-password.routes').then(r => r.FORGOT_PASSWORD_ROUTES),
  },

  // Routes to render mobile sidebars
  {
    path: 'mobile',
    component: MobileLayoutComponent,
    children: [
      {
        path: 'vip-level-badge',
        component: ProgramBadgeWrapperComponent,
      },
      {
        path: 'buy-sell',
        loadChildren: () => import('./routes/mobile-sidebar/mobile-buy-sell-list/mobile-buy-sell-list.routes').then(r => r.MOBILE_BUY_SELL_LIST_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'advanced-search-options',
        loadChildren: () =>
          import('./routes/mobile-sidebar/mobile-advanced-search-options/mobile-advanced-search-options.routes').then(
            r => r.MOBILE_ADVANCED_SEARCH_OPTIONS_ROUTES,
          ),
      },
      {
        path: 'user-profile',
        loadChildren: () => import('./routes/mobile-sidebar/mobile-user-profile/mobile-user-profile.routes').then(r => r.MOBILE_USER_PROFILE_ROUTES),
      },
      {
        path: 'save-search',
        loadChildren: () => import('./routes/mobile-sidebar/mobile-save-search/mobile-save-search.routes').then(r => r.MOBILE_SAVE_SEARCH_ROUTES),
        canActivate: [AuthGuard],
      },
    ],
  },

  // Routes that use masterpage go here
  // canActivate with AuthGuard determines if this is an authenticated only route
  {
    path: '',
    component: LayoutMainComponent,
    data: { showLayoutFooterForDesktop: true },
    children: [
      {
        path: 'houses-for-auction',
        data: { showLayoutFooterForDesktop: false },
        loadChildren: () => import('./routes/houses/houses.routes').then(r => r.HOUSES_ROUTES),
      },
      {
        path: 'state-licenses',
        loadChildren: () => import('./routes/state-licenses/state-licenses.routes').then(r => r.STATE_LICENSES_ROUTES),
      },
      {
        path: 'careers',
        loadChildren: () => import('./routes/careers/careers.routes').then(r => r.CAREERS_ROUTES),
      },
      {
        path: 'about-us',
        loadChildren: () => import('./routes/about-us/about-us.routes').then(r => r.ABOUT_US_ROUTES),
      },
      {
        path: 'contact-us',
        loadChildren: () => import('./routes/contact-us/contact-us.routes').then(r => r.CONTACT_US_ROUTES),
      },
      {
        path: 'help-center',
        loadChildren: () => import('./routes/help-center/help-center.routes').then(r => r.HELP_CENTER_ROUTING),
      },
      {
        path: 'terms-of-service',
        loadChildren: () => import('./routes/terms-of-service/terms-of-service.routes').then(r => r.TERMS_OF_SERVICE_ROUTES),
      },
      {
        path: 'privacy-policy',
        loadChildren: () => import('./routes/privacy-policy/privacy-policy.routes').then(r => r.PRIVACY_POLICY_ROUTES),
      },
      {
        path: 'register-agent',
        loadChildren: () => import('./routes/agent-registration/agent-registration.routes').then(r => r.AGENT_REGISTRATION_ROUTES),
      },
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('./routes/home/home.routes').then(r => r.HOME_ROUTES),
      },
      /**
       * AUTHED
       */

      {
        path: 'my-listings',
        loadChildren: () => import('./routes/my-listings/my-listings.routes').then(r => r.MY_LISTINGS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'verify-reo',
        loadChildren: () => import('./routes/verify-reo/verify-reo.routes').then(r => r.VERIFY_REO_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'my-customers',
        loadChildren: () => import('./routes/my-customers/my-customers.routes').then(r => r.MY_CUSTOMERS_ROUTES),
        canActivate: [AuthGuard],
      },

      {
        path: 'purchase-profile',
        loadChildren: () => import('./routes/new-purchase-profile/new-purchase-profile.routes').then(r => r.NEW_PURCHASE_PROFILE_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'purchased-properties',
        loadChildren: () => import('./routes/purchased-properties/purchased-properties.routes').then(r => r.PURCHASED_PROPERTIES_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'my-profile',
        loadChildren: () => import('./routes/my-profile/my-profile.routes').then(r => r.MY_PROFILE_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'saved-properties',
        loadChildren: () => import('./routes/saved-properties/saved-properties.routes').then(r => r.SAVED_PROPERTIES_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'current-bids',
        loadChildren: () => import('./routes/current-bids/current-bids.routes').then(r => r.CURRENT_BIDS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'bid-deposit-status',
        loadChildren: () => import('./routes/bid-deposit-status/bid-deposit-status.routes').then(r => r.BID_DEPOSIT_STATUS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'client-uploads',
        loadChildren: () => import('./routes/client-uploads/client-uploads.routes').then(r => r.CLIENT_UPLOADS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'all-documents',
        loadChildren: () => import('./routes/all-documents/all-documents.routes').then(r => r.ALL_DOCUMENTS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'daily-tasks',
        loadChildren: () => import('./routes/daily-tasks/daily-tasks.routes').then(r => r.DAILY_TASKS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'inventory-dashboard',
        loadChildren: () => import('./routes/inventory-dashboard/inventory-dashboard-routing.module').then(r => r.INVENTORY_DASHBOARD_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'program-managers',
        loadChildren: () => import('./routes/program-managers/program-managers.routes').then(r => r.PROGRAM_MANAGERS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'manage-properties',
        loadChildren: () => import('./routes/manage-properties/manage-properties.routes').then(r => r.MANAGE_PROPERTIES_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: 'property-details/:id',
        loadChildren: () => import('./routes/property-details/property-details.routes').then(r => r.PROPERTY_DETAILS_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: '**',
        component: NoContentComponent,
        data: { title: 'Page Not Found' },
      },
    ],
  },
];

export const APP_ROUTES_PROVIDER = [
  provideRouter(
    APP_ROUTES,
    environment.settings.preloadRoutes ? withPreloading(PreloadAllModules) : withPreloading(NoPreloading),
    withInMemoryScrolling({
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled',
    }),
    withEnabledBlockingInitialNavigation(),
    withRouterConfig({ onSameUrlNavigation: 'reload' }),
    withComponentInputBinding(),
  ),

  { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
];
